import React, { useContext, useEffect, useMemo, useState } from "react";
import DateFilterDropdown from "../../Shared/DateFilterDropdown/DateFilterDropdown";
import { InlineFilterSelect } from "../shared/FilterSelect";
import VehicleAllocationTab from "../tabs/VehicleAllocationTab";
import classes from "../MasterDashboard.module.css";
import { useTmsDashboardFilters } from "../hooks/useTmsDashboardFilters";
import { useVehicleAllocation } from "../hooks/useVehicleAllocation";
import { useVehicleAllocationTable } from "../hooks/useVehicleAllocationTable";
import useIsSuperAdmin from "../../../hooks/useIsSuperAdmin";
import { useMixPanelForTracking } from "../../../utils/TrackMixPanel";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../MyCompanyProfileContext";
import {
  ROLE_OPTIONS,
  PERIOD_PRESET_FILTERS,
  toIsoDate,
  formatRelativeTime,
  transformVehicleAllocation,
} from "../utils/transformers";

const VehicleAllocationPanel = ({ viewOnly = false, onRestrictedClick = () => {}, isActive = true }) => {
  const isSuperAdmin = useIsSuperAdmin();
  const roleOptions = isSuperAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((opt) => opt.value !== "company_view");
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: "None",
      Definition: "user has visited vehicle status tab",
    });
  }, []);

  const [role, setRole] = useState(ROLE_OPTIONS[0].value);
  useEffect(() => {
    if (!isSuperAdmin && role === "company_view") {
      setRole("user_view");
    }
  }, [isSuperAdmin, role]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return { startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) };
  });
  const [plant, setPlant] = useState("All");
  const [destination, setDestination] = useState("All");
  const [transporterId, setTransporterId] = useState("All");
  const [tableSkip, setTableSkip] = useState(0);
  const [dateFilterKey, setDateFilterKey] = useState(0);

  const { startDate, endDate } = dateRange;

  const handleDateChange = ({ fromDate, toDate, selectedFilter }) => {
    setDateRange({ startDate: toIsoDate(new Date(fromDate)), endDate: toIsoDate(new Date(toDate)) });
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: selectedFilter || `${fromDate} - ${toDate}`,
      Definition: "user has changed period filter",
    });
  };

  const handleRoleChange = (value) => {
    setRole(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: value,
      Definition: "user has changed role filter",
    });
  };

  const handlePlantChange = (value) => {
    setPlant(value);
    setDestination("All");
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: value,
      Definition: "user has changed plant filter",
    });
  };

  const handleDestinationChange = (value) => {
    setDestination(value);
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: value,
      Definition: "user has changed destination filter",
    });
  };

  const handleTransporterChange = (value) => {
    setTransporterId(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: value,
      Definition: "user has changed transporter filter",
    });
  };

  const handleResetFilters = () => {
    setRole(ROLE_OPTIONS[0].value);
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setDateRange({ startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) });
    setPlant("All");
    setDestination("All");
    setTransporterId("All");
    setDateFilterKey((k) => k + 1);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "vehicle status",
      Data: "None",
      Definition: "user has reset filters",
    });
  };

  const transporterIdParam = transporterId === "All" ? undefined : transporterId;
  const destinationParam = destination === "All" ? undefined : destination;
  const plantParam = plant === "All" ? undefined : plant;

  const { data: filtersData } = useTmsDashboardFilters({
    startDate,
    endDate,
    plant: plantParam,
    destination: destinationParam,
    transporterId: transporterIdParam,
    role,
    screen: "vehicle_allocation",
  });

  const plantOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.plants || []).map((p) => ({
        value: p.location_code || p.location_name,
        label: p.location_name,
        code: p.location_code,
      })),
    ],
    [filtersData]
  );

  const destinationOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.destinations || []).map((d) => ({
        value: d.location_code || d.location_name,
        label: d.location_name,
        code: d.location_code,
      })),
    ],
    [filtersData]
  );

  const transporterOptions = useMemo(() => {
    const transporters =
      filtersData?.transporters ||
      filtersData?.transporters_bid ||
      filtersData?.transporters_dispatched ||
      [];
    return [
      { value: "All", label: "All" },
      ...transporters.map((t) => ({ value: t.transporter_id, label: t.transporter_name })),
    ];
  }, [filtersData]);

  useEffect(() => {
    setTableSkip(0);
  }, [startDate, endDate, plant, destination, transporterId]);

  const { data: vehicleAllocationData, isLoading: isVehicleAllocationLoading, dataUpdatedAt } = useVehicleAllocation(
    {
      startDate,
      endDate,
      plant,
      destination: destinationParam,
      transporterId: transporterIdParam,
      role,
    },
    isActive
  );

  const vehicleAllocation = useMemo(
    () => (vehicleAllocationData ? transformVehicleAllocation(vehicleAllocationData) : null),
    [vehicleAllocationData]
  );

  const { data: vehicleAllocationTable, isLoading: isVehicleAllocationTableLoading } = useVehicleAllocationTable(
    {
      startDate,
      endDate,
      plant,
      destination: destinationParam,
      transporterId: transporterIdParam,
      role,
      skip: tableSkip,
      limit: 10,
    },
    isActive
  );

  const lastUpdated = formatRelativeTime(dataUpdatedAt, now);

  return (
    <>
      <div className={classes.controlsBar}>
        <InlineFilterSelect label="Role" value={role} onChange={handleRoleChange} options={roleOptions} />
        <div className={classes.inlineFilterGroup}>
          <span className={classes.inlineFilterLabel}>Period</span>
          <DateFilterDropdown
            key={dateFilterKey}
            onDateChange={handleDateChange}
            defaultFilter="1 Week"
            presetFilters={PERIOD_PRESET_FILTERS}
          />
        </div>
        <InlineFilterSelect label="Plant" value={plant} onChange={handlePlantChange} options={plantOptions} />
        <InlineFilterSelect
          label="Destination"
          value={destination}
          onChange={handleDestinationChange}
          options={destinationOptions}
        />
        <InlineFilterSelect
          label="Transporter"
          value={transporterId}
          onChange={handleTransporterChange}
          options={transporterOptions}
        />
        <button
          type="button"
          className={classes.resetFiltersButton}
          onClick={handleResetFilters}
          title="Reset all filters"
        >
          ↺ Reset
        </button>
        <div className={classes.liveStatus}>
          <span className={classes.liveDot} />
          Live · updated {lastUpdated}
        </div>
      </div>

      <div className={classes.pageBody}>
        {isVehicleAllocationLoading || (isVehicleAllocationTableLoading && !vehicleAllocationTable) ? (
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner} />
            <div className={classes.loadingText}>Loading vehicle allocation…</div>
          </div>
        ) : vehicleAllocation ? (
          <VehicleAllocationTab
            vehicleAllocation={vehicleAllocation}
            table={vehicleAllocationTable}
            onTablePageChange={setTableSkip}
            exportFilters={{
              startDate,
              endDate,
              plant: plant === "All" ? undefined : plant,
              destination: destinationParam,
              transporterId: transporterIdParam,
              role,
            }}
            viewOnly={viewOnly}
            onRestrictedClick={onRestrictedClick}
          />
        ) : null}
      </div>
    </>
  );
};

export default VehicleAllocationPanel;
